import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'
import PropTypes from 'prop-types'
import { TextEllipsis } from '~/components'

import classNames from 'classnames/bind'
import styles from '../styles/Menu.module.css'
import FoodPrice from './FoodPrice'
import { EXTERNAL_PARTNER, MERCHANT_TYPE } from '~/constants'
import { selectRestaurant } from '~/redux/restaurantSlice'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { InView } from 'react-intersection-observer'
import _ from 'lodash'
import { getParentType, getRootMarginViewport } from '~/utils/logEventUser'
import { CHILD_TYPE, USER_ACTIONS } from '~/constants/logEventUser'
import { logEventUserApi } from '~/apis/logEventUser'
import STORAGE_KEYS from '~/constants/localStorage'
import { selectPopupTopPage } from '~/redux/popupTopPageSlice'
import { useAppContext } from '~/contexts/AppContext'
import { isBuffetDishType, isDisabledBuffetItem } from '~/utils/buffet'
import { selectQuantityBuffetDishCart } from '~/redux/cartSlice'
import { isHidePlusFoodEnable } from '~/utils'
const cx = classNames.bind(styles)

/**
 * Component dùng chung cho 2 template:
 * Single Dish
 * Double Dish
 */
const MenuItem = (props) => {
  const {
    id,
    name,
    description,
    original_price,
    discount_amount,
    price,
    display_price,
    active,
    image,
    stock_quantity: stockQuantity,
    isDoubleList,
    food_type,
  } = props
  const { handleOpenPopupFoodDetail, heightHeaderFixed } = useAppContext()
  const { externalPartner, restaurantId, tableId, merchant_type, extraFeatures } =
    useSelector(selectRestaurant)
  const quantityBuffetDishInCart = useSelector(selectQuantityBuffetDishCart) // Lấy ra số lượng buffet dish được adđ trong giỏ hàng
  const isPartnerSmaregi = externalPartner === EXTERNAL_PARTNER.Smaregi
  const isTypeHotel = merchant_type === MERCHANT_TYPE.HOTEL
  const isPartnerSukiya = externalPartner === EXTERNAL_PARTNER.Sukiya
  const { i18n } = useTranslation()
  const currentLanguage = i18n.language
  const idTimer = useRef()

  const [isInview, setIsInview] = useState(false) // lưu lại trạng thái của item khi ở trong view hoặc ra khỏi view
  const { open } = useSelector(selectPopupTopPage)
  const isHidePlusFoodItem = isHidePlusFoodEnable(extraFeatures) && price === 0

  const isUnOrderable = () => {
    return (
      isSoldOut() ||
      !active ||
      (isBuffetDishType(food_type) && isDisabledBuffetItem(quantityBuffetDishInCart))
    )
  }

  // Hàm xử lý log sự kiện của user click/view vào món ăn
  const handleLogEventUser = useCallback(
    async ({ action }) => {
      await logEventUserApi({
        parent_type: getParentType(merchant_type),
        parent_id: restaurantId,
        child_type: CHILD_TYPE.menu,
        child_id: id,
        locale: currentLanguage,
        position_id: tableId || null,
        action,
      })
    },
    [currentLanguage, id, merchant_type, restaurantId, tableId],
  )

  /**
   * + Log sự kiện impression của user khi nằm trong view
   * + Trong trường hợp user thay đổi ngôn ngữ cũng log lại nếu nằm trong view
   * + Chỉ log trong trường hợp đã chọn ngôn ngữ
   */
  useEffect(() => {
    // Trong trường hợp là hotel mà chưa chọn phòng -> không log
    if ((isTypeHotel && !tableId) || open) return
    // Trường hợp smaregi nhưng chưa nhập số người -> không log
    const numberOfPeopleLocal = localStorage.getItem(STORAGE_KEYS.NUMBER_OF_PEOPLE)
    if (!numberOfPeopleLocal && isPartnerSmaregi) return

    const localeFromLocal = localStorage.getItem(STORAGE_KEYS.LOCALE)

    if (idTimer.current) clearTimeout(idTimer.current)

    if (isInview && localeFromLocal) {
      idTimer.current = setTimeout(() => {
        handleLogEventUser({
          action: USER_ACTIONS.impression,
        })
      }, 2000)
    }
  }, [
    isInview,
    name,
    currentLanguage,
    handleLogEventUser,
    isTypeHotel,
    tableId,
    open,
    isPartnerSmaregi,
  ])

  const handleChangeView = (inview) => {
    setIsInview(inview)
  }

  const onClickItem = () => {
    if (isUnOrderable()) return
    handleLogEventUser({
      action: USER_ACTIONS.click,
    })

    handleOpenPopupFoodDetail({ idFood: id })
  }

  const renderThumb = () => {
    const classThumbWrap = cx({
      food_thumb_wrap_t1: true,
      food_thumb_wrap_t2: isDoubleList,
    })

    const classThumb = cx({
      food_thumb: true,
    })

    return (
      <Box className={classThumbWrap}>
        <img src={image} alt="" className={classThumb} />
      </Box>
    )
  }

  const renderName = () => {
    return (
      <TextEllipsis
        component="h3"
        lineHeight="1.5"
        className={cx('food_name')}
        color="baseColor.colorText1"
        numberLine={2}>
        {name}
      </TextEllipsis>
    )
  }

  const renderDescription = () => {
    return (
      <TextEllipsis
        component="p"
        numberLine={2}
        lineHeight="1.5"
        color="baseColor.colorText1"
        className={cx('food_desc')}>
        {description}
      </TextEllipsis>
    )
  }

  // Lấy ra giá món ăn. Nếu trường hợp là Sukiya thì sẽ lấy trường display_price
  const getPrice = () => {
    if (isPartnerSukiya) return display_price

    return price
  }

  // Hiển thị giá của món ăn bao gồm giá gốc và giá sau khi khuyến mãi
  const renderPrice = () => {
    if (isHidePlusFoodItem) return null

    return (
      <FoodPrice
        price={getPrice()}
        original_price={original_price}
        discount_amount={discount_amount}
      />
    )
  }

  const isSoldOut = () => {
    return stockQuantity === 0
  }

  const classFoodItemWrap = cx({
    food_item_wrap: true,
    food_item_wrap_t2: isDoubleList,
  })

  const classFoodItem = cx({
    food_item_disabled: isUnOrderable(),
  })

  // Trong trường hợp smaregi và món ăn là buffet type thì sẽ không hiển thị. Vì chỉ cho phép order buffet từ phía merchant
  if (isPartnerSmaregi && food_type && isBuffetDishType(food_type)) return null

  return (
    <InView
      rootMargin={getRootMarginViewport(heightHeaderFixed)}
      as="div"
      threshold={1}
      className={classFoodItemWrap}
      onChange={handleChangeView}>
      <Box className={classFoodItem} onClick={onClickItem} bgcolor="baseColor.color1">
        <Box className={cx('food_image_wrap')}>{renderThumb()}</Box>
        <Box>
          {renderName()}
          {renderDescription()}
          {renderPrice()}
        </Box>
      </Box>
    </InView>
  )
}

MenuItem.propTypes = {
  id: PropTypes.number,
  name: PropTypes.string,
  description: PropTypes.string,
  price: PropTypes.number,
  display_price: PropTypes.number,
  original_price: PropTypes.number,
  discount_amount: PropTypes.number,
  active: PropTypes.bool,
  stock_quantity: PropTypes.number,
  image: PropTypes.string,
  isDoubleList: PropTypes.bool,
  food_type: PropTypes.string,
}

export default React.memo(MenuItem)
